import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import {
  Badge,
  Button,
  IconHighlight,
  ModalLayout,
  SearchInput,
  SelectableChip,
  StatusMessage,
} from '@aimsos/ui';

import {
  ALL_COLUMN_TYPES,
  COLUMN_TYPE_PILLS,
  useColumnPicker,
} from '../hooks/useColumnPicker.js';
import { useModalEscape } from '../hooks/useModalEscape.js';
import { useScrollableModalBody } from '../hooks/useScrollableModalBody.js';
import ColumnPickerRow from './ColumnPickerRow.jsx';
import { ModalFooter } from './parts';
import { PickerError, PickerSkeleton } from './PickerStates.jsx';

import { Columns3, SearchX } from 'lucide-react';

/**
 * `Columns to expose` (CT-5195 / DS-3).
 *
 * Per epic decision 8 this toggles **availability only** — adding or removing an
 * entity's columns is Data Studio's job, so there is no add/remove affordance
 * here on purpose.
 *
 * `ModalLayout` + `useBodyPortal`: no `createPortal`, no scroll lock, no focus
 * trap, no second `Esc` handler.
 */
/** No unsupported fields — the usual case. */
const EMPTY_COLUMNS = Object.freeze([]);

function ColumnPickerModal({
  isOpen,
  columns,
  unsupportedColumns = EMPTY_COLUMNS,
  committedSelection,
  isLoading,
  error,
  onRetry,
  onClose,
  onApply,
}) {
  const { t } = useTranslation('dashboards');
  const { bodyClassName, bodyProps } = useScrollableModalBody();

  const {
    draftSelected,
    searchTerm,
    activeType,
    visibleGroups,
    visibleColumns,
    selectedCount,
    totalCount,
    toggle,
    selectAll,
    deselectAll,
    setSearchTerm,
    setActiveType,
    resetDraft,
  } = useColumnPicker({ columns, committedSelection, isOpen });

  // The same search narrows the unsupported ones, so a search never hides
  // that a field exists.
  const visibleUnsupported = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return unsupportedColumns;
    return unsupportedColumns.filter((column) => `${column.displayName} ${column.internalName}`
      .toLowerCase().includes(needle));
  }, [searchTerm, unsupportedColumns]);

  const handleCancel = useCallback(() => {
    resetDraft();
    onClose?.();
  }, [onClose, resetDraft]);

  const handleApply = useCallback(() => onApply?.(draftSelected), [draftSelected, onApply]);

  const handleSearchChange = useCallback(
    (event) => setSearchTerm(event.target.value),
    [setSearchTerm],
  );

  const handleClearSearch = useCallback(() => setSearchTerm(''), [setSearchTerm]);

  useModalEscape(isOpen, handleCancel);
  if (!isOpen) return null;

  const rows = () => {
    if (isLoading) return <PickerSkeleton count={6} height={60} />;
    if (error) {
      return (
        <PickerError
          title={t('widgetBuilder.filters.errorTitle')}
          subtitle={t('widgetBuilder.filters.errorSubtitle')}
          retryLabel={t('widgetBuilder.data.retry')}
          onRetry={onRetry}
        />
      );
    }
    if (visibleColumns.length === 0 && visibleUnsupported.length === 0) {
      return (
        <div className="flex flex-col items-center gap-3">
          <StatusMessage
            icon={SearchX}
            title={t('widgetBuilder.columnPicker.emptyTitle')}
            subtitle={t('widgetBuilder.columnPicker.emptySubtitle')}
          />
          <Button variant="secondary" size="sm" onClick={handleClearSearch}>
            {t('widgetBuilder.entityPicker.clearSearch')}
          </Button>
        </div>
      );
    }

    /*
      One block per source entity (CT-5529). A single unnamed group — a
      dataset's columns, or an entity with no composite behind it — renders
      without a heading, which is the plain list this was before.
    */
    const columnRows = (groupColumns) => (
      <div className="flex flex-col gap-2">
        {groupColumns.map((column) => (
          <ColumnPickerRow
            key={column.fieldUuid}
            column={column}
            typeLabel={t(`widgetBuilder.columnPicker.types.${column.type}`)}
            checked={draftSelected.includes(column.fieldUuid)}
            onToggle={toggle}
          />
        ))}
      </div>
    );

    /*
      Mapped on the composite but not buildable yet (CT-5480): shown so the
      mapping is visible, never selectable.

      A sibling of the groups rather than part of `columnRows`, for two
      reasons. It belongs to no entity, so rendering it inside the per-group
      list repeated it under every heading. And it has to survive a search
      that leaves no supported column: the groups are empty then, and a block
      nested in them would disappear with the field it is there to reveal.
    */
    const unsupportedRows = visibleUnsupported.length ? (
      <div className="flex flex-col gap-2">
        <p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-content-secondary">
          {t('widgetBuilder.columnPicker.unsupportedTitle')}
        </p>
        {visibleUnsupported.map((column) => (
          <ColumnPickerRow
            key={column.fieldUuid}
            column={column}
            typeLabel={t(`widgetBuilder.columnPicker.types.${column.type}`)}
            disabledReason={t('widgetBuilder.columnPicker.unsupportedReason')}
            onToggle={toggle}
          />
        ))}
      </div>
    ) : null;

    if (visibleGroups.length === 1 && !visibleGroups[0].label) {
      return (
        <div className="flex flex-col gap-4">
          {columnRows(visibleGroups[0].columns)}
          {unsupportedRows}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        {visibleGroups.map((group) => (
          <div key={group.entityUuid} className="flex flex-col gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-xs font-medium uppercase tracking-wide text-content-muted">
                {group.label}
              </span>
              {group.role && group.role !== 'PRIMARY' ? (
                <Badge size="small" variant="secondary">
                  {t(`widgetBuilder.columnPicker.groupRole.${group.role.toLowerCase()}`, {
                    defaultValue: group.role,
                  })}
                </Badge>
              ) : null}
            </div>
            {columnRows(group.columns)}
          </div>
        ))}
        {unsupportedRows}
      </div>
    );
  };

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleCancel}
      useBodyPortal
      fillHeight
      maxWidthClassName="max-w-2xl"
      titlePrefix={<IconHighlight icon={Columns3} bgColor="Primary" iconColor="PrimaryAlt" size="medium" />}
      title={t('widgetBuilder.columnPicker.title')}
      subtitle={t('widgetBuilder.columnPicker.subtitle')}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-col gap-3 px-6 pb-3 pt-4">
          <SearchInput
            value={searchTerm}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
            placeholder={t('widgetBuilder.columnPicker.searchPlaceholder')}
            aria-label={t('widgetBuilder.columnPicker.searchPlaceholder')}
          />

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-1.5">
              {COLUMN_TYPE_PILLS.map((type) => (
                <SelectableChip
                  key={type}
                  size="m"
                  selected={activeType === type}
                  onClick={() => setActiveType(type)}
                >
                  {type === ALL_COLUMN_TYPES
                    ? t('widgetBuilder.columnPicker.allTypes')
                    : t(`widgetBuilder.columnPicker.types.${type}`)}
                </SelectableChip>
              ))}
            </div>

            <Button
              variant="tertiary"
              size="sm"
              onClick={selectedCount === 0 ? selectAll : deselectAll}
            >
              {selectedCount === 0
                ? t('widgetBuilder.columnPicker.selectAll')
                : t('widgetBuilder.columnPicker.deselectAll')}
            </Button>
          </div>
        </div>

        <div className={`${bodyClassName} px-6 pb-4`} {...bodyProps}>
          {rows()}
        </div>

        <ModalFooter
          cancelLabel={t('widgetBuilder.columnPicker.cancel')}
          onCancel={handleCancel}
          statusText={t('widgetBuilder.columnPicker.counter', { selected: selectedCount, total: totalCount })}
          primaryAction={
            <Button variant="primary" onClick={handleApply} className="!text-[12px]">
              {t('widgetBuilder.columnPicker.done')}
            </Button>
          }
        />
      </div>
    </ModalLayout>
  );
}

ColumnPickerModal.propTypes = {
  isOpen: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  unsupportedColumns: PropTypes.arrayOf(PropTypes.object),
  committedSelection: PropTypes.arrayOf(PropTypes.string).isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  onRetry: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onApply: PropTypes.func,
};

export default ColumnPickerModal;
